/**
 * LibraryItemRow — shared row component for library panels.
 *
 * Renders a saved library item with thumbnail, name (inline-editable),
 * a confirm-apply popover, and a three-dots context menu with Rename,
 * Overwrite, Duplicate, and Delete actions.
 *
 * Used by PoseLibraryPanel, HandPoseLibraryPanel, and CharacterProfilePanel.
 */

import {
  Check,
  Copy,
  MoreHorizontal,
  Pencil,
  RefreshCw,
  Trash2,
} from "lucide-react";
import type { ReactNode } from "react";
import { useRef, useState } from "react";
import { Button, IconButton } from "./Button.tsx";
import { MenuItem } from "./menu/MenuItem.tsx";
import { MenuDivider } from "./menu/MenuPrimitives.tsx";
import { Popover } from "./Popover.tsx";
import { PANEL_INPUT_STYLE } from "./styles.ts";
import {
  COLOR_ACCENT,
  COLOR_TEXT_DIM,
  COLOR_TEXT_FAINT,
  COLOR_TEXT_SECONDARY,
  FONT_SIZE_BODY,
  FONT_SIZE_SMALL,
  FONT_SIZE_TINY,
  RADIUS_SM,
} from "./tokens.ts";

export interface LibraryItemRowProps {
  id: string;
  name: string;
  /** Optional subtitle below the name (e.g. date). */
  subtitle?: string;
  /** Thumbnail URL (if available). */
  thumbnailUrl?: string | null;
  /** Thumbnail size in pixels (default 32). */
  thumbnailSize?: number;
  /** Fallback icon when no thumbnail. */
  thumbnailFallback?: ReactNode;
  /** Label for the confirm-apply button (e.g. "Apply Pose"). */
  applyLabel: string;
  /** Called when the user confirms applying this item. */
  onApply: () => void;
  /** Called with the new name when a rename is committed. */
  onRename: (newName: string) => void;
  /** Called when the user selects "Overwrite". */
  onOverwrite: () => void;
  /** Called when the user selects "Duplicate". */
  onDuplicate: () => void;
  /** Called when the user selects "Delete". */
  onDelete: () => void;
}

export function LibraryItemRow({
  name,
  subtitle,
  thumbnailUrl,
  thumbnailSize = 32,
  thumbnailFallback,
  applyLabel,
  onApply,
  onRename,
  onOverwrite,
  onDuplicate,
  onDelete,
}: LibraryItemRowProps) {
  const [confirming, setConfirming] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [renaming, setRenaming] = useState(false);
  const [renameValue, setRenameValue] = useState("");
  const rowRef = useRef<HTMLDivElement>(null);
  const menuBtnRef = useRef<HTMLButtonElement>(null);
  const renameInputRef = useRef<HTMLInputElement>(null);

  const commitRename = () => {
    const trimmed = renameValue.trim();
    if (trimmed) onRename(trimmed);
    setRenaming(false);
  };

  return (
    <div
      ref={rowRef}
      onClick={() => {
        if (renaming) return;
        setConfirming(true);
        setMenuOpen(false);
      }}
      style={{
        display: "flex",
        alignItems: "center",
        gap: 6,
        padding: "4px 4px",
        borderRadius: RADIUS_SM,
        cursor: renaming ? "default" : "pointer",
      }}
    >
      {/* Thumbnail */}
      <div
        style={{
          width: thumbnailSize,
          height: thumbnailSize,
          borderRadius: RADIUS_SM,
          background: "rgba(0,0,0,0.3)",
          flexShrink: 0,
          overflow: "hidden",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        {thumbnailUrl ? (
          <img
            src={thumbnailUrl}
            alt=""
            style={{ width: "100%", height: "100%", objectFit: "cover" }}
            draggable={false}
          />
        ) : (
          (thumbnailFallback ?? null)
        )}
      </div>

      {/* Name or rename input */}
      {renaming ? (
        <input
          ref={renameInputRef}
          value={renameValue}
          onChange={(e) => setRenameValue(e.target.value)}
          onClick={(e) => e.stopPropagation()}
          onKeyDown={(e) => {
            if (e.key === "Enter") commitRename();
            if (e.key === "Escape") setRenaming(false);
          }}
          onBlur={commitRename}
          style={{ ...PANEL_INPUT_STYLE, fontSize: FONT_SIZE_TINY }}
        />
      ) : (
        <div style={{ flex: 1, minWidth: 0 }}>
          <div
            style={{
              fontSize: FONT_SIZE_SMALL,
              color: COLOR_TEXT_SECONDARY,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {name}
          </div>
          {subtitle && (
            <div style={{ fontSize: FONT_SIZE_TINY, color: COLOR_TEXT_FAINT }}>
              {subtitle}
            </div>
          )}
        </div>
      )}

      {/* Three-dots menu button */}
      {!renaming && (
        <IconButton
          ref={menuBtnRef}
          onClick={(e) => {
            e.stopPropagation();
            setMenuOpen((v) => !v);
            setConfirming(false);
          }}
          title="More actions"
          label="More actions"
          icon={<MoreHorizontal size={14} />}
          style={{ color: COLOR_TEXT_DIM }}
        />
      )}

      {/* Confirm-apply popover */}
      {confirming && (
        <Popover
          open
          anchorRef={rowRef as React.RefObject<HTMLElement>}
          onClose={() => setConfirming(false)}
          style={{ padding: 8 }}
        >
          <Button
            data-popover-item
            onClick={(e) => {
              e.stopPropagation();
              onApply();
              setConfirming(false);
            }}
            variant="ghost"
            size="sm"
            leftIcon={<Check size={14} />}
            style={{
              padding: "6px 14px",
              color: COLOR_ACCENT,
              fontSize: FONT_SIZE_BODY,
            }}
          >
            {applyLabel}
          </Button>
        </Popover>
      )}

      {/* Three-dots context menu */}
      {menuOpen && (
        <Popover
          open
          anchorRef={menuBtnRef as React.RefObject<HTMLElement>}
          onClose={() => setMenuOpen(false)}
        >
          <MenuItem
            label="Rename"
            icon={<Pencil size={13} />}
            shortcutKey="r"
            onClick={() => {
              setRenaming(true);
              setRenameValue(name);
              setMenuOpen(false);
              requestAnimationFrame(() => {
                renameInputRef.current?.focus();
                renameInputRef.current?.select();
              });
            }}
          />
          <MenuItem
            label="Overwrite"
            icon={<RefreshCw size={13} />}
            shortcutKey="o"
            onClick={() => {
              onOverwrite();
              setMenuOpen(false);
            }}
          />
          <MenuItem
            label="Duplicate"
            icon={<Copy size={13} />}
            shortcutKey="u"
            onClick={() => {
              onDuplicate();
              setMenuOpen(false);
            }}
          />
          <MenuDivider />
          <MenuItem
            label="Delete"
            icon={<Trash2 size={13} />}
            shortcutKey="d"
            danger
            onClick={() => {
              onDelete();
              setMenuOpen(false);
            }}
          />
        </Popover>
      )}
    </div>
  );
}
